import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { ApplicationState } from '../../store/application-store';
import { Observable, of } from 'rxjs';
import { takeWhile, tap, take, filter } from 'rxjs';
import { WidgetComponent } from '../../dashboards/components/widget-component/widget-component';
import { ITable, ITableColumn, IResizedColumn } from '../../common/models/table';
import { IColumnDefinition } from '../../common/models/column-definition';
import { IContext } from '../../context/models/context';
import { ActivatedRoute } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { RuleSet } from 'ngx-angular-query-builder';
import { QueryBuilderConfigProvider } from '../../common/filter/query-builder-config-provider';
import { QueryBuilderConfig } from 'ngx-angular-query-builder';
import { FilterEditorDialogResult } from '../../common/filter/models/filter-editor-dialog-result';
import * as filterActions from '../../common/filter/actions/filter.actions';
import * as filterUtils from '../../common/filter/filter-utils';
import { ColumnFormatterService } from '../../common/services/column-formatter.service';
import { MojoWellKnownNames } from '@common/constants/mojo-well-known-names';

import { PaymentsToYouService } from '@feature/payments-to-you/services/payments-to-you.service';
import { RouteContextService } from '@context/services/route-context.service';
import { FilterService } from '@common/filter/services/filter.service';
import { UserSettingsService } from '@feature/user-settings/services/user-settings.service';
import { PaymentsToYouToastService } from '@payments-to-you/services/payments-to-you-toast-service';
import { DashboardIdentifiers } from '@dashboards/dashboard-identifiers';
import { OidcAuthService } from '@auth/services/oidc-auth.service';
import { RemittanceDocument } from '@feature/payments-to-you/models/remittance-document';
import { ColumnDefinitionFormatType } from '@common/enums/column-definition-format-type';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'mojo-payments-to-you-widget',
  templateUrl: './payments-to-you-table-widget.component.html'
})
export class PaymentsToYouWidgetComponent extends WidgetComponent implements OnInit, OnDestroy {

  context: IContext;
  table$: Observable<ITable>;
  loading$: Observable<boolean>;
  error$: Observable<string>;
  availableColumns$: Observable<IColumnDefinition[]>;
  configProvider: QueryBuilderConfigProvider;
  isAlive: boolean = true;
  currentFilter: RuleSet = filterUtils.getDefaultFilter();
  queryConfig: Observable<QueryBuilderConfig> = of(({ fields: {} }));
  rows: number = 8;
  adminFilterText: string = 'Showing all events';
  @ViewChild('printSection') printSection: ElementRef;
  remittanceDocument: Observable<RemittanceDocument>;

  get hasCustomFilter(): boolean {
    return filterUtils.isNotDefaultFilter(this.currentFilter);
  }

  getEmptyFilter(): RuleSet {
    return filterUtils.getDefaultFilter();
  }

  constructor(
    protected store: Store<ApplicationState>,
    protected readonly confirmationService: ConfirmationService,
    private readonly paymentsToYouService: PaymentsToYouService,
    private readonly routeContextService: RouteContextService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly columnFormatterService: ColumnFormatterService,
    private readonly filterService: FilterService,
    private readonly userSettingsService: UserSettingsService,
    private readonly paymentsToYouToastService: PaymentsToYouToastService,
    private readonly oidcAuthService: OidcAuthService) {

    super(store, confirmationService);

  }

  ngOnInit(): void {
    this.availableColumns$ = this.paymentsToYouService.getColumns(this.widget);

    this.table$ = this.paymentsToYouService.getTable(this.widget);

    this.userSettingsService.getUserSettings().pipe(
      take(1),
      tap(us => {

        if (us.Product === MojoWellKnownNames.Products.MojoForSuppliers) {

          const context = this.routeContextService.getSupplierContextFromRoute(this.activatedRoute);
          this.paymentsToYouService.loadExternalPayments(this.widget, context.AccountRef);

          if (!us.IsInPreviewMode && !this.paymentsToYouToastService.messageDisplayed) {
            const userProfile = this.oidcAuthService.getUserProfileImmediate();
            this.paymentsToYouService.getNoOfNewPayments(this.widget, userProfile.previousLoginTime)
              .pipe(
                filter(x => x != undefined),
                take(1)
              ).subscribe((noOfNewPayments: number) => {
                  if (!this.paymentsToYouToastService.messageDisplayed) {
                    this.paymentsToYouToastService.newPayments(noOfNewPayments);
                  }
                }
              );
          }

        } else {

          const context = this.routeContextService.getContextFromRoute(this.activatedRoute);
          this.paymentsToYouService.loadInternalPayments(this.widget, context);

        }


      })).subscribe();



    this.paymentsToYouService.loadColumns(this.widget);

    this.filterService.loadFilters(this.widget);

    this.queryConfig = this.paymentsToYouService.getQueryConfig(this.widget);

    this.paymentsToYouService.getUserFilter(this.widget)
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((filter) => {
        this.currentFilter = filter;
      });


    this.paymentsToYouService.getUserFilter(this.widget).subscribe((filter) => {
      this.currentFilter = filter;
      this.adminFilterText = this.filterService.getFilterString(filter, 'Showing payments for', 'Showing all');
    });
  }



  isColumnRightAlign(columnType: number): boolean {
    const result = (columnType === ColumnDefinitionFormatType.Currency ||
      columnType === ColumnDefinitionFormatType.Numeric0Dp ||
      columnType === ColumnDefinitionFormatType.Numeric2Dp);
    return result;
  }


  ngOnDestroy() {
    this.isAlive = false;
  }

  dialogSaved(columns: IColumnDefinition[]) {

    this.userSettingsService.getCurrentProduct().pipe(tap((product: string) => {
      if (product === MojoWellKnownNames.Products.MojoForSuppliers) {
        const context = this.routeContextService.getSupplierContextFromRoute(this.activatedRoute);
        this.paymentsToYouService.updateMfsColumns(this.widget, columns, context.AccountRef);
      } else {
        const context = this.routeContextService.getContextFromRoute(this.activatedRoute);
        this.paymentsToYouService.updateMojoColumns(this.widget, columns, context);
      }
    }),
      take(1)).subscribe();
  }

  resetColumns() {
    this.userSettingsService.getCurrentProduct().pipe(tap((product: string) => {
      if (product === MojoWellKnownNames.Products.MojoForSuppliers) {
        const context = this.routeContextService.getSupplierContextFromRoute(this.activatedRoute);
        this.paymentsToYouService.resetMfsColumns(this.widget, context.AccountRef);
      } else {
        const context = this.routeContextService.getContextFromRoute(this.activatedRoute);
        this.paymentsToYouService.resetMojoColumns(this.widget, context);
      }
    }),
      take(1)).subscribe();
  }

  hasRemittances(rowData: any): boolean {
    return rowData['SystemRemittanceAvailable'] === "y";
  }

  columnsResized(resizedColumn: IResizedColumn) {
    this.paymentsToYouService.resizedColumns(this.widget, resizedColumn);
  }

  columnsReordered(selectedColumns: string[]) {
    this.paymentsToYouService.reorderColumns(this.widget, selectedColumns);
  }

  filterEdited(result: FilterEditorDialogResult): void {
    this.store.dispatch(new filterActions.UpdateWidgetFilter(new filterActions.WidgetFilterActionPayload(this.widget.id, result.filter)));
  }

  formatByColumnType(rowData, columnDefinition: ITableColumn): string {
    return this.columnFormatterService.formatByColumnType(rowData, columnDefinition);
  }

  getColSize(size) {
    return size + 'px';
  }

  print(rowdata) {
    const hasRemittances = rowdata['SystemRemittanceAvailable'];
    if (hasRemittances === "y") {
      const cashBookUri: number = rowdata['SystemCashBookUri'];
      this.remittanceDocument = this.paymentsToYouService.getRemittanceDocument(cashBookUri);
    }
  }


}
